import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import type { SectionsSignaturesType, SignatureBox } from 'services/entities/SignatureTypes';
import { useSocketClient } from 'providers/SocketContext';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import ProposifyTheme from '../../../muiTheme/PyTheme';
import { SectionEvents } from '../../../services/socket/SocketEvents';
import { dragDropManager } from '../dndContext';
import { addGridBlockState, resetSections, selectSections, setInitialState } from 'components/editor/grid/reduxStore/editorSlice';
import { useAppDispatch, useAppSelector } from '../grid/reduxStore/Store';
import { useEditorConfig } from '../GridDndEditor/Block/Text/EditorConfigs';
import { convertSectionsLoadDataToContentBlocks, collectSignaturesFromSections } from '../GridDndEditor/gridHelper';
import { useClearBlockSelection } from '../GridDndEditor/useClearBlockSelection';
import { AITokenProvider } from '../GridDndEditor/TiptapEditor/TipTapBarMenu/AI';
import { useBlockStyleSettings } from '../hooks/useBlockSettings';
import Sections from '../Sections';
import type { SectionContentCollectionType } from '../Sections/Section.model';
import { SectionsDrawerProvider } from '../SectionsDrawer/SectionsDrawerContext';
import SectionsList from '../SectionsDrawer/SectionsList';
import { SidePanelProvider } from '../SidePanel/content';
import '../styles.less';
import '../variables/variablesList.less';
import './DocumentBuilder.less';
import {
  DocumentBuilderEditorMainContent,
  DocumentBuilderLeftPanel,
  DocumentBuilderMainContent,
  DocumentBuilderRightPanel,
} from './DocumentBuilderLayout';
import { DocumentSettingsProvider } from 'components/editor/SidePanel/DocumentSettings/DocumentDesignSettings/DocumentSettingsContext';
import { GridBlockType } from 'services/entities/BlockTypes';
import { setSaveStatus } from 'components/editor/grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from 'components/editor/shared/models/DocumentSaveStatus';
import DocumentBuilderHeader from './DocumentBuilderHeader';
import { MuiEditorSidePanel } from 'components/editor/SidePanel/MuiSidePanel/MuiEditorSidePanel';
import { ThreadPanel } from 'components/editor/GridDndEditor/TiptapEditor/components/Comments/ThreadPanel';
import { useComments } from 'providers/CommentsContext';
import { selectContentId, selectContentType, selectIsDocumentLocked } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { ContentType } from 'utils/getContentType';
import { useCombinedVariablesQueries } from 'services/variables/useVariablesQuery';
import { setVariables, setCrmIsError } from 'components/editor/grid/reduxStore/allVariableSlice';
import { syncCrmRows } from 'components/editor/grid/reduxStore/crmCountSync';
import { useTableContentUndoRedo } from 'components/editor/hooks/useTableContentUndoRedo';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';
import { useCrmPricingEmbedFlagStatus } from 'hooks/useCrmPricingEmbedFlagStatus';
import { useCrmVariablesV2RouteFlag } from 'hooks/useCrmVariablesV2RouteFlag';
import { useAppNativeVariablesV2RouteFlag } from 'hooks/useAppNativeVariablesV2RouteFlag';
import { useV2VariableRoutesFlag } from 'hooks/useV2VariableRoutesFlag';

const DocumentBuilder: React.FC = () => {
  const { socketClient, connect, isConnected, isReconnecting, hasPendingRequests } = useSocketClient();
  const { commentsEnabled } = useComments();
  const { setIsSignatureLoaded } = useEditorConfig();
  const sections = useAppSelector(selectSections);
  const sectionsRef = useRef(sections);
  const isInitialSectionLoad = useRef(true);
  const contentId = useAppSelector(selectContentId);
  const contentType = useAppSelector(selectContentType);
  const isDocumentLocked = useAppSelector(selectIsDocumentLocked);

  const crmPricingEmbedEnabled = useCrmPricingEmbedFlag();
  const { isLoading: crmPricingEmbedFlagLoading } = useCrmPricingEmbedFlagStatus();
  const { enabled: crmVariablesV2RouteEnabled, isLoading: crmVariablesV2RouteFlagLoading } = useCrmVariablesV2RouteFlag();
  const appNativeVariablesV2RouteFlag = useAppNativeVariablesV2RouteFlag();
  const customFieldShapeEnabled = useV2VariableRoutesFlag();
  const { data: variables, errors: variablesErrors } = useCombinedVariablesQueries(
    contentId,
    contentType,
    crmPricingEmbedEnabled,
    crmVariablesV2RouteEnabled,
    crmVariablesV2RouteFlagLoading,
    crmPricingEmbedFlagLoading,
    { customFieldShapeEnabled, appNativeVariablesV2Route: appNativeVariablesV2RouteFlag }
  );

  const [pendingSignatures, setPendingSignatures] = useState<SectionsSignaturesType | null>(null);
  const pendingSignaturesRef = useRef<SectionsSignaturesType | null>(null);

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    pendingSignaturesRef.current = pendingSignatures;
  }, [pendingSignatures]);

  const dispatch = useAppDispatch();
  const { handleTableContentChanged } = useTableContentUndoRedo();

  useEffect(() => {
    if (contentType === ContentType.SECTION) {
      return;
    }
    dispatch(setVariables(variables));
    dispatch(setCrmIsError(variablesErrors.crmError));
  }, [variables, variablesErrors, contentType, dispatch]);

  useEffect(() => {
    syncCrmRows({
      contentType,
      isLocked: isDocumentLocked,
      crmFetchFailed: variablesErrors.crmError,
      crmPricingEmbedEnabled,
      crmVariablesV2RouteEnabled,
      sections: sectionsRef.current,
      variablesRoot: variables,
      handleTableContentChanged,
    });
  }, [
    variables,
    variablesErrors,
    contentType,
    isDocumentLocked,
    crmPricingEmbedEnabled,
    crmVariablesV2RouteEnabled,
    handleTableContentChanged,
  ]);

  const { loadBlockStyles } = useBlockStyleSettings();

  const clearBlockSelection = useClearBlockSelection();

  const sectionInitialLoadCallback = ({ content: sectionsLoadData }: { content: SectionContentCollectionType | null }) => {
    if (!isInitialSectionLoad.current && hasPendingRequests()) {
      return;
    }

    isInitialSectionLoad.current = false;

    if (!sectionsLoadData || sectionsLoadData.sections.length === 0) {
      dispatch(setInitialState({ sections: [], temporaryDimensions: {} }));
      return;
    }

    loadBlockStyles(sectionsLoadData.sections);
    const signaturesToMerge: Record<string, SignatureBox[]> | undefined =
      pendingSignaturesRef.current ?? collectSignaturesFromSections(sectionsRef.current);

    const blocksState = convertSectionsLoadDataToContentBlocks(sectionsLoadData, signaturesToMerge);
    dispatch(setInitialState(blocksState));
  };

  useEffect(() => {
    socketClient.getAllSignaturesOnMount((data) => {
      setPendingSignatures(data);
      setIsSignatureLoaded(true);
    });

    connect();

    return () => {
      socketClient.disconnect();
      dispatch(resetSections());
      dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED, errCode: '' }));
    };
  }, []);

  useEffect(() => {
    if (pendingSignatures && sections.length > 0) {
      Object.entries(pendingSignatures).forEach((entry) => {
        const [sectionId, signatures] = entry as [string, SignatureBox[]];
        const section = sections.find((s) => s.id === sectionId);
        if (!section) return;

        signatures.forEach((signature) => {
          if (!section.blocksContent[signature.signatureBoxId]) {
            const blockConfig = {
              id: signature.signatureBoxId,
              x: signature.properties.position.x,
              y: signature.properties.position.y,
              width: signature.properties.dimensions.width,
              height: signature.properties.dimensions.height,
              z: section.blocksLayer.greaterZIndexAvailable,
            };

            dispatch(
              addGridBlockState({
                sectionId,
                blockId: signature.signatureBoxId,
                content: signature,
                blockConfig,
                blockType: GridBlockType.SIGNATURE,
              })
            );
          }
        });
      });

      setPendingSignatures(null);
    }
  }, [pendingSignatures, sections, dispatch]);

  useEffect(() => {
    if (isConnected && !isReconnecting.current) {
      socketClient.publish(SectionEvents.SECTION_LOAD, {}, sectionInitialLoadCallback);
    }
  }, [isConnected]);

  const onMouseDown = () => {
    clearBlockSelection();
  };

  const [isAiTabActive, setIsAiTabActive] = useState(false);

  return (
    <ThemeProvider theme={ProposifyTheme}>
      <CssBaseline />
      <SectionsDrawerProvider>
        <AITokenProvider>
          <DocumentBuilderHeader />
          <DndProvider manager={dragDropManager}>
            <DocumentSettingsProvider>
              <SidePanelProvider>
                {contentType !== ContentType.SECTION && (
                  <DocumentBuilderLeftPanel>
                    <SectionsList />
                  </DocumentBuilderLeftPanel>
                )}
                <DocumentBuilderMainContent>
                  <DocumentBuilderEditorMainContent>
                    <Container data-testid="editor-document-main-content" onMouseDown={onMouseDown}>
                      <Sections />
                    </Container>
                  </DocumentBuilderEditorMainContent>
                  {commentsEnabled && <ThreadPanel contentId={contentId} />}
                </DocumentBuilderMainContent>
                <DocumentBuilderRightPanel isAiTabActive={isAiTabActive}>
                  <MuiEditorSidePanel setIsAiTabActive={setIsAiTabActive} />
                </DocumentBuilderRightPanel>
              </SidePanelProvider>
            </DocumentSettingsProvider>
          </DndProvider>
        </AITokenProvider>
      </SectionsDrawerProvider>
    </ThemeProvider>
  );
};

export default DocumentBuilder;
